/**
 * Fleet state for Machines: GET /api/fleet, polled while a view that needs it is mounted (every
 * 4 s while the page is visible, every 1.5 s while a rollout or an update is moving), and right
 * after every change. In demo mode (?mock=1) it runs on local data with simulated progress.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { STALE_MS, activeUpdatePause, inventoryCsv, machineHealth } from '@orgus/protocol';
import type { AgentRing, FleetHealthReport, FleetInventory, FleetState, FleetVersionsReport, Machine, UpdateChannel, UpdatePolicyInput } from '@orgus/protocol';
import { api } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { mockFleet } from './mock.ts';

const POLL_MS = 4_000;
const FAST_POLL_MS = 1_500;

interface FleetStore {
  state: FleetState | null;
  error: unknown;
  loading: boolean;
  fetchedAt: number | null;
  set(s: FleetState): void;
}

export const useFleetStore = create<FleetStore>()((set) => ({
  state: null,
  error: null,
  loading: true,
  fetchedAt: null,
  set: (s) => set({ state: s, error: null, loading: false, fetchedAt: Date.now() }),
}));

const isMock = () => useOrgus.getState().mode === 'mock';

export async function refreshFleet(): Promise<void> {
  if (isMock()) {
    const st = useFleetStore.getState();
    const machines = Object.values(useOrgus.getState().machines);
    if (!st.state && machines.length) {
      const f = mockFleet(machines);
      st.set(f);
      // The demo canary already runs the new version: say so in the machine list too.
      const a = useOrgus.getState();
      const changed = machines.filter((m) => f.machines.some((x) => x.machineId === m.id && x.version !== m.agentVersion)).map((m) => ({ ...m, agentVersion: f.machines.find((x) => x.machineId === m.id)!.version }));
      if (changed.length) a.applyPatch({ at: Date.now(), upserts: { machines: changed }, removes: {} });
    }
    else useFleetStore.setState({ fetchedAt: Date.now() });
    return;
  }
  try {
    useFleetStore.getState().set(await api<FleetState>('/api/fleet'));
  } catch (e) {
    useFleetStore.setState({ error: e, loading: false });
  }
}

const moving = (s: FleetState | null) =>
  !!s && (s.rollout?.status === 'running' || s.machines.some((m) => m.update && !['done', 'failed', 'rolled-back'].includes(m.update.stage)));

/** Subscribe a view to the fleet state (polls while mounted). */
export function useFleet() {
  const state = useFleetStore((s) => s.state);
  const error = useFleetStore((s) => s.error);
  const loading = useFleetStore((s) => s.loading);
  const fetchedAt = useFleetStore((s) => s.fetchedAt);
  const mode = useOrgus((s) => s.mode);
  const machineCount = useOrgus((s) => Object.keys(s.machines).length);
  const fast = moving(state);
  useEffect(() => {
    void refreshFleet();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void refreshFleet();
    }, fast ? FAST_POLL_MS : POLL_MS);
    return () => clearInterval(t);
  }, [mode, fast, machineCount > 0]);
  return { state, error, loading, fetchedAt, refresh: refreshFleet };
}

// ── Demo-mode changes ─────────────────────────────────────────────────────────

function local(change: (s: FleetState) => FleetState): FleetState {
  const s = useFleetStore.getState().state;
  if (!s) throw new Error('The demo fleet is not loaded yet.');
  const next = change(s);
  useFleetStore.getState().set(next);
  return next;
}

const later = (ms: number, fn: () => void) => setTimeout(() => isMock() && fn(), ms);

/** Demo: a machine finishes its update, and its agent version changes in the main store too. */
function mockFinish(machineId: string, version: string): void {
  const a = useOrgus.getState();
  const m = a.machines[machineId];
  if (m) a.applyPatch({ at: Date.now(), upserts: { machines: [{ ...m, agentVersion: version }] }, removes: {} });
  local((s) => {
    let rollout = s.rollout;
    if (rollout) {
      const targets = rollout.targets.map((t) => (t.machineId === machineId ? { ...t, state: 'done' as const, at: Date.now() } : t));
      const testOpen = targets.some((t) => t.ring === 'test' && t.state !== 'done' && t.state !== 'skipped');
      const anyOpen = targets.some((t) => t.state === 'pending' || t.state === 'offered' || t.state === 'updating');
      const status = rollout.stage === 'test' && !testOpen ? (targets.some((t) => t.ring === 'fleet' && t.state === 'pending') ? 'awaiting-promotion' : 'done') : rollout.stage === 'fleet' && !anyOpen ? 'done' : rollout.status;
      rollout = { ...rollout, targets, status };
    }
    const history = rollout && rollout.status === 'done' ? [{ ...rollout, endedAt: Date.now() }, ...s.history] : s.history;
    return {
      ...s,
      rollout: rollout && rollout.status === 'done' ? null : rollout,
      history,
      machines: s.machines.map((x) => (x.machineId === machineId ? { ...x, version, drift: 'current', update: { rolloutId: s.rollout?.id ?? 'up_demo', version, stage: 'done', at: Date.now() } } : x)),
    };
  });
}

let simulated = false;
/** Demo: the test machine finishes a few seconds after the page opens. */
export function startMockSimulation(): void {
  if (simulated || !isMock()) return;
  simulated = true;
  later(6_000, () => {
    const s = useFleetStore.getState().state;
    const t = s?.rollout?.targets.find((x) => x.ring === 'test' && x.state === 'updating');
    if (s?.rollout && t) mockFinish(t.machineId, s.rollout.version);
  });
}

// ── Changes ───────────────────────────────────────────────────────────────────

const accept = (s: FleetState) => {
  useFleetStore.getState().set(s);
  return s;
};

/** Demo: a machine's health from the local fleet state, as the hub would compute it. */
function mockHealth(m: Machine, s: FleetState | null): FleetHealthReport {
  const now = Date.now();
  const f = s?.machines.find((x) => x.machineId === m.id) ?? null;
  return { machineId: m.id, name: m.name, ...machineHealth({ machine: m, fleet: f, latest: s?.latest ?? null, drained: false, staleMs: STALE_MS.machine, now }), at: now };
}

/** Save text as a file in the browser's downloads. */
function saveFile(name: string, text: string, type: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type }));
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

/**
 * Central management reads (R-ND-2): each is a POST so the hub audits who looked and what they
 * saw. In demo mode they run on local data and write nothing.
 */
export const fleetReports = {
  async checkHealth(machineId: string): Promise<FleetHealthReport> {
    if (isMock()) {
      const m = useOrgus.getState().machines[machineId];
      if (!m) throw new Error('That machine is not in the demo.');
      return mockHealth(m, useFleetStore.getState().state);
    }
    return api<FleetHealthReport>(`/api/fleet/machines/${encodeURIComponent(machineId)}/health`, { method: 'POST', body: {} });
  },
  async checkVersions(): Promise<FleetVersionsReport> {
    if (isMock()) {
      const s = useFleetStore.getState().state;
      const counts = new Map<string, FleetVersionsReport['versions'][number]>();
      for (const f of s?.machines ?? []) {
        const c = counts.get(f.version) ?? { version: f.version, drift: f.drift, machines: 0 };
        c.machines += 1;
        counts.set(f.version, c);
      }
      return { at: Date.now(), latest: s?.latest ?? null, feed: s?.feed ?? null, versions: [...counts.values()].sort((a, b) => b.machines - a.machines), behind: s?.machines.filter((f) => f.drift === 'behind').length ?? 0 };
    }
    const r = await api<FleetVersionsReport>('/api/fleet/versions', { method: 'POST', body: {} });
    await refreshFleet();
    return r;
  },
  /** Export the inventory as CSV; resolves to the number of machines in it. */
  async exportInventory(): Promise<number> {
    let inv: FleetInventory;
    if (isMock()) {
      const s = useFleetStore.getState().state;
      inv = {
        at: Date.now(),
        latest: s?.latest ?? null,
        rows: Object.values(useOrgus.getState().machines).map((m) => {
          const f = s?.machines.find((x) => x.machineId === m.id);
          return { machineId: m.id, name: m.name, hostname: m.hostname, os: m.os, arch: m.arch, version: f?.version ?? m.agentVersion, drift: f?.drift ?? 'unknown', channel: f?.channel ?? 'stable', ring: f?.ring ?? 'fleet', health: mockHealth(m, s).status, online: m.online, lastSeen: m.lastSeen, drained: false, updatable: f?.updatable ?? null, service: f?.service ?? null, labels: m.labels };
        }),
      };
    } else inv = await api<FleetInventory>('/api/fleet/inventory', { method: 'POST', body: {} });
    saveFile(`orgus-machines-${new Date(inv.at).toISOString().slice(0, 10)}.csv`, `﻿${inventoryCsv(inv)}`, 'text/csv');
    return inv.rows.length;
  },
};

export const fleetActions = {
  async setRing(machineId: string, ring: AgentRing): Promise<void> {
    if (isMock()) return void local((s) => ({ ...s, machines: s.machines.map((m) => (m.machineId === machineId ? { ...m, ring } : m)) }));
    accept(await api<FleetState>(`/api/fleet/machines/${encodeURIComponent(machineId)}`, { method: 'PUT', body: { ring } }));
  },
  async updateMachine(machineId: string, version?: string): Promise<void> {
    if (isMock()) {
      const v = version ?? useFleetStore.getState().state?.latest ?? '0.1.1';
      local((s) => ({ ...s, machines: s.machines.map((m) => (m.machineId === machineId ? { ...m, update: { rolloutId: 'up_demo', version: v, stage: 'downloading', at: Date.now() } } : m)) }));
      later(4_000, () => mockFinish(machineId, v));
      return;
    }
    accept(await api<FleetState>(`/api/fleet/machines/${encodeURIComponent(machineId)}/update`, { method: 'POST', body: version ? { version } : {} }));
  },
  async startRollout(version: string): Promise<void> {
    if (isMock()) {
      local((s) => {
        const targets = s.machines
          .filter((m) => m.version !== version)
          .map((m) => ({ machineId: m.machineId, ring: m.ring, from: m.version, state: (m.updatable === false ? 'skipped' : m.ring === 'test' ? 'offered' : 'pending') as 'skipped' | 'offered' | 'pending', at: Date.now() }));
        const stage = targets.some((t) => t.ring === 'test' && t.state !== 'skipped') ? 'test' : 'fleet';
        return { ...s, rollout: { id: `ro_${Date.now().toString(36)}`, version, startedBy: 'owner', startedAt: Date.now(), stage, status: 'running', targets: targets.map((t) => (stage === 'fleet' && t.state === 'pending' ? { ...t, state: 'offered' as const } : t)) } };
      });
      for (const t of useFleetStore.getState().state?.rollout?.targets ?? []) if (t.state === 'offered') later(4_000, () => mockFinish(t.machineId, version));
      return;
    }
    accept(await api<FleetState>('/api/fleet/rollouts', { method: 'POST', body: { version } }));
  },
  async promote(rolloutId: string): Promise<void> {
    if (isMock()) {
      const r = local((s) => (s.rollout ? { ...s, rollout: { ...s.rollout, stage: 'fleet', status: 'running', targets: s.rollout.targets.map((t) => (t.ring === 'fleet' && t.state === 'pending' && useOrgus.getState().machines[t.machineId]?.online ? { ...t, state: 'offered' as const } : t)) } } : s)).rollout;
      for (const t of r?.targets ?? []) if (t.state === 'offered') later(4_500, () => mockFinish(t.machineId, r!.version));
      return;
    }
    accept(await api<FleetState>(`/api/fleet/rollouts/${encodeURIComponent(rolloutId)}/promote`, { method: 'POST' }));
  },
  /** A machine's own channel, or null to follow the workspace. */
  async setChannel(machineId: string, channel: UpdateChannel | null): Promise<void> {
    if (isMock()) {
      return void local((s) => ({ ...s, machines: s.machines.map((m) => (m.machineId === machineId ? { ...m, channelOverride: channel, channel: channel ?? s.policy?.channel ?? 'stable' } : m)) }));
    }
    accept(await api<FleetState>(`/api/fleet/machines/${encodeURIComponent(machineId)}`, { method: 'PUT', body: { channel } }));
  },
  async savePolicy(input: UpdatePolicyInput): Promise<void> {
    if (isMock()) {
      return void local((s) => {
        if (!s.policy) return s;
        const freezes = input.freezes?.map((f, i) => ({ id: f.id ?? `frz_demo_${i}`, from: f.from, until: f.until, reason: f.reason, by: 'owner' })) ?? s.policy.freezes;
        const policy = { ...s.policy, ...input, freezes, updatedAt: Date.now(), updatedBy: 'owner' };
        return { ...s, policy, auto: s.auto ? { ...s.auto, on: policy.mode === 'auto' && !activeUpdatePause(policy, Date.now()) } : s.auto };
      });
    }
    accept(await api<FleetState>('/api/fleet/policy', { method: 'PUT', body: input }));
  },
  async pause(o: { until: number | null; reason?: string }): Promise<void> {
    if (isMock()) {
      return void local((s) => (s.policy ? { ...s, policy: { ...s.policy, paused: { by: 'owner', at: Date.now(), until: o.until, ...(o.reason ? { reason: o.reason } : {}) } }, auto: s.auto ? { ...s.auto, on: false } : s.auto } : s));
    }
    accept(await api<FleetState>('/api/fleet/policy/pause', { method: 'POST', body: o }));
  },
  async resume(): Promise<void> {
    if (isMock()) {
      return void local((s) => (s.policy ? { ...s, policy: { ...s.policy, paused: null }, auto: s.auto ? { ...s.auto, on: s.policy.mode === 'auto' } : s.auto } : s));
    }
    accept(await api<FleetState>('/api/fleet/policy/resume', { method: 'POST' }));
  },
  async cancel(rolloutId: string): Promise<void> {
    if (isMock()) {
      local((s) => (s.rollout ? { ...s, rollout: null, history: [{ ...s.rollout, status: 'cancelled', endedAt: Date.now(), note: 'Cancelled by owner. Machines already updated stay updated.' }, ...s.history] } : s));
      return;
    }
    accept(await api<FleetState>(`/api/fleet/rollouts/${encodeURIComponent(rolloutId)}/cancel`, { method: 'POST' }));
  },
};
